import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner, Alert, Button } from "react-bootstrap";
import ModalComponent from "../../components/ModalComponent/ModalComponent";
import Stepper from "./Stepper";
import BaseInfoUniversal from "./ProviderBasicInfo/BasicInfo";
import DocumentUpload from "./DocumentUpload";
import Politicas from "./Politicas";
import FinishStep from "./FinishStep";
import FormularioBloqueado from "./FormularioBloqueado";
import useFormAccessGuard from "../../hooks/useFormAccessGuard";
import "../../styles.css";
import "../../App.css";
import "./providerForm.css";

const API_URL = process.env.REACT_APP_API_URL || "https://apiprov.conduto.com.ec";

const UserHomepage = () => {
  const { type, step } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idFormularioFromUrl = searchParams.get("id_formulario");

  const currentStep = parseInt(step) || 1;
  const [formularioEstado, setFormularioEstado] = useState(null);
  const [formularioInfo, setFormularioInfo] = useState(null);
  const [estadoAcceso, setEstadoAcceso] = useState(null);
  const [verificandoAcceso, setVerificandoAcceso] = useState(true);
  const [errorVerificacion, setErrorVerificacion] = useState(null);

  const [modalShown, setModalShown] = useState(false);
  const [formularioNotificado, setFormularioNotificado] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estaEnRango, setEstaEnRango] = useState(false);

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(`formData_${type}`);
    const parsed = savedData ? JSON.parse(savedData) : {};
    return {
      providerInfo: {},
      files: [],
      certifications: [],
      completedSteps: [],
      formCompleted: false,
      ...parsed,
      id_formulario: idFormularioFromUrl ? parseInt(idFormularioFromUrl) : (parsed.id_formulario || null),
    };
  });

  const { allowed, loading: loadingAccess } = useFormAccessGuard(idFormularioFromUrl, type);

  const isValidType = (tipo) => {
    return tipo === "natural" || tipo === "juridica";
  };

  const isFormularioDentroDeRango = (formulario) => {
    if (!formulario) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const parseFechaLocal = (fechaStr) => {
      if (!fechaStr) return null;
      const [year, month, day] = fechaStr.split("-");
      return new Date(year, month - 1, day);
    };
    const fechaInicio = parseFechaLocal(formulario.fecha_inicio);
    const fechaFin = parseFechaLocal(formulario.fecha_fin);
    return (!fechaInicio || today >= fechaInicio) && (!fechaFin || today <= fechaFin);
  };

  const verificarFormularioExistente = async (formId) => {
    try {
      const response = await fetch(`${API_URL}/api/formularios/${formId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const formulario = await response.json();
        const notificado = formulario.requisitos_aceptados === 1 || formulario.requisitos_aceptados === true;
        setFormularioNotificado(notificado);
        setFormStatus(formulario.estado);
        const rangoValido = isFormularioDentroDeRango(formulario);
        setEstaEnRango(rangoValido);
        if (!notificado && formulario.estado !== 'finalizado' && rangoValido) {
          setModalShown(true);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error al verificar formulario:", error);
      return false;
    }
  };

  useEffect(() => {
    const inicializar = async () => {
      setLoading(true);
      if (idFormularioFromUrl) {
        await verificarFormularioExistente(idFormularioFromUrl);
      }
      setLoading(false);
    };
    inicializar();
  }, [idFormularioFromUrl]);

  useEffect(() => {
    const verificarAccesoFormulario = async () => {
      if (!idFormularioFromUrl) {
        setVerificandoAcceso(false);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/formularios/${idFormularioFromUrl}/estado-acceso`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
          }
        );
        if (response.ok) {
          const data = await response.json();
          setEstadoAcceso(data);
          if (!data.acceso_permitido) {
            setFormularioInfo({
              id_formulario: idFormularioFromUrl,
              titulo: data.titulo,
              fecha_inicio: data.fecha_inicio,
              fecha_fin: data.fecha_fin
            });
          }
        } else {
          setErrorVerificacion(`Error al verificar el estado del formulario: ${response.status}`);
        }
      } catch (error) {
        setErrorVerificacion("Error de conexión al verificar el formulario");
      } finally {
        setVerificandoAcceso(false);
      }
    };
    verificarAccesoFormulario();
  }, [idFormularioFromUrl, type]);

  useEffect(() => {
    const checkActiveForm = async () => {
      try {
        let response;
        if (idFormularioFromUrl) {
          response = await fetch(`${API_URL}/api/providers/form-data/${idFormularioFromUrl}`, {
            credentials: "include",
          });
        } else {
          response = await fetch(`${API_URL}/api/providers/active-form`, {
            credentials: "include",
          });
        }
        if (response.ok) {
          const data = await response.json();
          if (data.formulario) {
            setFormularioEstado(data.formulario.estado);
            setFormData((prev) => ({
              ...prev,
              id_formulario: data.formulario.id_formulario,
              providerInfo: data.proveedor_datos || {},
            }));
            if (!type && data.usuario?.ultima_ruta) {
              const match = data.usuario.ultima_ruta.match(/^\/form\/(natural|juridica)\/(\d+)$/);
              if (match) {
                const [, tipo, paso] = match;
                navigate(`/form/${tipo}/${paso}${idFormularioFromUrl ? `?id_formulario=${idFormularioFromUrl}` : ''}`);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al verificar formulario:", error);
      }
    };
    if (allowed && estadoAcceso?.acceso_permitido !== false) checkActiveForm();
  }, [type, currentStep, navigate, idFormularioFromUrl, allowed, estadoAcceso]);

  const completeForm = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/providers/complete-form/${formData.id_formulario}`,
        { method: "PUT", credentials: "include" }
      );
      if (response.ok) {
        setFormData((prev) => ({ ...prev, formCompleted: true }));
        localStorage.removeItem(`formData_${type}`);
        navigate("/form-completed");
      }
    } catch (error) {
      console.error("Error completing form:", error);
    }
  };

  const handleStepCompletion = (step) => {
    const updatedCompletedSteps = [...new Set([...formData.completedSteps, step])];
    const newFormData = { ...formData, completedSteps: updatedCompletedSteps };
    setFormData(newFormData);
    localStorage.setItem(`formData_${type}`, JSON.stringify(newFormData));
  };

  const nextStep = async () => {
    const newStep = currentStep + 1;
    const nuevaRuta = `/form/${type}/${newStep}${idFormularioFromUrl ? `?id_formulario=${idFormularioFromUrl}` : ''}`;
    const idToUpdate = idFormularioFromUrl ? parseInt(idFormularioFromUrl) : formData.id_formulario;
    try {
      const token = localStorage.getItem("token");
      const responseUpdateStep = await fetch(
        `${API_URL}/api/providers/update-step/${idToUpdate}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
          body: JSON.stringify({ step: newStep }),
        }
      );
      if (!responseUpdateStep.ok) {
        const errorData = await responseUpdateStep.json();
        throw new Error(errorData.detail || "Error al avanzar paso");
      }
      await fetch(`${API_URL}/api/formularios/actualizar-ruta`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ ruta: nuevaRuta }),
      });
      handleStepCompletion(currentStep);
      navigate(nuevaRuta);
    } catch (error) {
      console.error("Error al avanzar paso:", error);
      alert(error.message);
    }
  };

  const prevStep = () => {
    const newRoute = `/form/${type}/${currentStep - 1}${idFormularioFromUrl ? `?id_formulario=${idFormularioFromUrl}` : ''}`;
    navigate(newRoute);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      providerInfo: {
        ...formData.providerInfo,
        [name]: type === "checkbox" ? checked : value,
      },
    };
    setFormData(newFormData);
    localStorage.setItem(`formData_${type}`, JSON.stringify(newFormData));
  };

  const baseSteps = [
    {
      title: "Información Proveedor",
      component: (
        <BaseInfoUniversal
          formData={formData}
          handleChange={handleChange}
          nextStep={nextStep}
          setFormData={setFormData}
          isReadOnly={formularioEstado === 'finalizado'}
          idFormulario={idFormularioFromUrl}
        />
      ),
      validate: () => !!formData.providerInfo.ruc,
    },
    {
      title: "Archivos",
      component: (
        <DocumentUpload
          nextStep={nextStep}
          prevStep={prevStep}
          isReadOnly={formularioEstado === 'finalizado'}
          idFormulario={idFormularioFromUrl}
          ruc={formData.providerInfo?.ruc}
        />
      ),
      validate: () => formData.licencias && formData.licencias.length > 0,
    },
    {
      title: "Políticas",
      component: (
        <Politicas
          formData={formData}
          handleChange={handleChange}
          nextStep={nextStep}
          prevStep={prevStep}
          isReadOnly={formularioEstado === 'finalizado'}
          idFormulario={idFormularioFromUrl}
        />
      ),
      validate: () => !!formData.configuraciones,
    },
  ];

  const steps = formularioEstado === 'finalizado'
    ? baseSteps
    : [
      ...baseSteps,
      {
        title: "Finalizar",
        component: <FinishStep formData={formData} prevStep={prevStep} type={type} idFormulario={idFormularioFromUrl} />,
        validate: () => true,
      },
    ];

  const handleResetSelection = () => navigate("/form");

  const renderNotFoundMessage = () => (
    <div className="text-center">
      <div className="mb-4" style={{ fontSize: "4rem" }}>📄</div>
      <h3 className="mb-3">Formulario no encontrado</h3>
      <p className="text-muted mb-4">
        No se ha encontrado el formulario solicitado o no tienes acceso a él.
      </p>
      <button
        onClick={() => navigate("/form")}
        className="btn btn-primary"
        style={{ backgroundColor: "#10407D", borderColor: "#10407D" }}
      >
        Volver al inicio
      </button>
    </div>
  );

  if (loading || verificandoAcceso || loadingAccess) {
    return (
      <div className="homepage min-vh-100 d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (errorVerificacion) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error de verificación</Alert.Heading>
          <p>{errorVerificacion}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={() => navigate("/proveedor/form")}>
              Volver a Mis Formularios
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (estadoAcceso && !estadoAcceso.acceso_permitido) {
    return (
      <FormularioBloqueado
        motivo={estadoAcceso.motivo_bloqueo}
        codigo={estadoAcceso.codigo_bloqueo}
        formularioInfo={formularioInfo}
      />
    );
  }

  if (!allowed) {
    return (
      <div className="container mt-5">
        <Alert variant="warning">
          <Alert.Heading>Acceso denegado</Alert.Heading>
          <p>No tienes permiso para acceder a este formulario.</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={() => navigate("/proveedor/form")}>
              Volver a Mis Formularios
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!type || !isValidType(type)) {
    return (
      <div className="homepage min-vh-100 py-5">
        <div className="container" style={{ maxWidth: "90%" }}>
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="card" style={{ border: "none", borderRadius: "8px" }}>
                <div className="card-body p-5">
                  {renderNotFoundMessage()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage min-vh-100 py-5">
      {formStatus !== 'finalizado' && estaEnRango && modalShown && (
        <ModalComponent
          onClose={() => setModalShown(false)}
          idFormulario={idFormularioFromUrl}
        />
      )}

      {formStatus !== 'finalizado' && estaEnRango && (
        <button
          onClick={() => setModalShown(true)}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#10407D",
            color: "#fff",
            border: "none",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            zIndex: 2000,
            fontSize: "20px",
            cursor: "pointer",
          }}
          title="Ver requisitos"
        >
          ?
        </button>
      )}

      <div className="container" style={{ maxWidth: "90%" }}>
        <div className="row justify-content-center">
          <div className="col-12 position-relative">
            <button
              onClick={handleResetSelection}
              className="btn rounded-circle"
              style={{
                position: "fixed",
                top: "65px",
                left: "25px",
                width: "40px",
                height: "40px",
                border: "1px solid #10407D",
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                backgroundColor: "#10407D",
              }}
              title="Volver"
            >
              <div style={{ color: "#fff", fontSize: "1.5rem" }}><i class="bi bi-arrow-left"></i></div>
            </button>

            <div
              className="card"
              style={{
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                minHeight: "100%",
              }}
            >
              <div className="card-body">
                {formData.formCompleted ? (
                  <div>Redirigiendo...</div>
                ) : (
                  <>
                    <Stepper steps={steps} currentStep={currentStep} />
                    <div className="mt-4">{steps[currentStep - 1]?.component}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserHomepage;